import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Group,
  Loader,
  Menu,
  Switch,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBuildingStore,
  IconGripVertical,
  IconPencil,
  IconPhoto,
  IconTrash,
} from "@tabler/icons-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageLightbox } from "@/components/ImageLightbox";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";

export function SortableCategoryRow({
  category,
  productCount = 0,
  sortable = true,
  toggling = false,
  onToggle,
  onEdit,
  onEditImage,
  onDelete,
  branches = [],
  branchVisible,
  onToggleBranch,
  onToggleAllBranches,
  togglingBranchId,
}) {
  const { can } = useAccess();
  // Katalogu düzenlemek (ad, görsel, sıra, genel aktiflik) categories:write ister; kategoriyi
  // BİR ŞUBEDE gizlemek ise bir şube override'ı — API'de prices:update'e bağlı. Şube müdürü
  // ikinciyi yapabilmeli, birincisini yapmamalı.
  const canWrite = can("categories");
  const canBranchOverride = can("products") || can("categories");

  const anyBranchHidden = branches.some(
    (branch) => !branchVisible?.(category.id, branch.id),
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    disabled: !sortable || !canWrite,
  });
  const [zoomed, { open: openZoom, close: closeZoom }] = useDisclosure(false);
  const hasImage = Boolean(category.imageUrl);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    background: isDragging ? "var(--mantine-color-chocolate-0)" : undefined,
  };

  return (
    <Group
      ref={setNodeRef}
      style={style}
      wrap="nowrap"
      gap="md"
      px="sm"
      py="xs"
      bd="1px solid var(--mantine-color-gray-1)"
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        {...attributes}
        {...listeners}
        disabled={!sortable || !canWrite}
        style={{
          cursor: sortable && canWrite ? "grab" : "not-allowed",
          touchAction: "none",
        }}
      >
        <IconGripVertical size={18} />
      </ActionIcon>
      <Avatar
        src={category.imageUrl}
        radius="md"
        size={40}
        component={hasImage ? "button" : undefined}
        type={hasImage ? "button" : undefined}
        aria-label={hasImage ? `${category.name} görselini büyüt` : undefined}
        onClick={hasImage ? openZoom : undefined}
        style={
          hasImage
            ? {
                cursor: "zoom-in",
                border: "none",
                padding: 0,
                background: "transparent",
              }
            : undefined
        }
        title={hasImage ? "Büyüt" : undefined}
      >
        {category.name?.[0]?.toUpperCase()}
      </Avatar>
      {hasImage && (
        <ImageLightbox
          opened={zoomed}
          onClose={closeZoom}
          src={category.imageUrl}
          alt={category.name}
        />
      )}
      <Text fw={500} style={{ flex: 1, minWidth: 0 }} truncate>
        {category.name}
      </Text>
      <Box w={70} style={{ display: "flex", justifyContent: "center" }}>
        <Text size="xs" c="dimmed">
          {productCount} ürün
        </Text>
      </Box>
      <Box w={44} style={{ display: "flex", justifyContent: "center" }}>
        <Switch
          checked={category.isActive}
          disabled={toggling || !canWrite}
          onChange={(event) => onToggle(category, event.currentTarget.checked)}
          onClick={(event) => event.stopPropagation()}
        />
      </Box>
      <Box w={90} style={{ display: "flex", justifyContent: "center" }}>
        {branches.length && category.isActive ? (
          <Menu shadow="md" position="bottom" withinPortal width={280}>
            <Menu.Target>
              <ActionIcon
                variant="light"
                color={anyBranchHidden ? "gray" : "teal"}
                title="Şube görünürlüğü"
              >
                <IconBuildingStore size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Hangi şubede görünsün?</Menu.Label>
              {canBranchOverride && (
                <>
                  <Group gap="xs" px="sm" pb="xs" grow>
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="teal"
                      onClick={() => onToggleAllBranches(category, true)}
                    >
                      Tümünü aç
                    </Button>
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="gray"
                      onClick={() => onToggleAllBranches(category, false)}
                    >
                      Tümünü kapat
                    </Button>
                  </Group>
                  <Menu.Divider />
                </>
              )}
              {branches.map((branch) => (
                <Menu.Item
                  key={branch.id}
                  closeMenuOnClick={false}
                  component="div"
                >
                  <Group justify="space-between" gap="md" wrap="nowrap">
                    <Text size="sm">{branch.name}</Text>
                    {togglingBranchId === branch.id ? (
                      <Loader size="xs" color="teal" />
                    ) : (
                      <Switch
                        checked={branchVisible(category.id, branch.id)}
                        color="teal"
                        size="xs"
                        disabled={!canBranchOverride}
                        onChange={(event) => {
                          const { checked } = event.currentTarget;
                          onToggleBranch(category, branch.id, checked);
                        }}
                      />
                    )}
                  </Group>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : (
          <ActionIcon
            variant="light"
            color="gray"
            disabled
            title={!branches.length ? "Şube yok" : "Genel durum pasif"}
          >
            <IconBuildingStore size={18} />
          </ActionIcon>
        )}
      </Box>
      <Box w={16} />
      {/* Yetkisiz kullanıcıda butonlar gizlenmiyor, kilitleniyor: satır düzeni her kullanıcıda
                aynı kalsın ve "böyle bir işlem var ama sana kapalı" görünsün. */}
      <Group w={108} gap={4} justify="center" wrap="nowrap">
        <ActionIcon
          variant="subtle"
          color="gray"
          {...lockProps(canWrite, { title: "Görsel" })}
          onClick={() => onEditImage(category)}
        >
          <IconPhoto size={18} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          {...lockProps(canWrite, { title: "Düzenle" })}
          onClick={() => onEdit(category)}
        >
          <IconPencil size={18} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="red"
          {...lockProps(canWrite, { title: "Sil" })}
          onClick={() => onDelete(category)}
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Group>
    </Group>
  );
}
